import React, { FC, ReactNode, useState } from 'react'
import papaparse from 'papaparse'
import { Column } from 'react-table'
import { Box } from '@chakra-ui/react'
import { t, Trans } from '@lingui/macro'

import { WorkbookStatusEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { formatDateShort } from 'front-end/src/utils/dateHelpers'

import { notification } from '../../../components/common/notifications/Notifications'
import IconTick from '../../../components/common/icon/IconTick'
import TableExtendedOld, {
  DEFAULT_TR_PROPS
} from '../../../components/common/table-extended-old/TableExtendedOld'
import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import { downloadAsFile } from '../../../utils/downloadAsFile'
import { PathwayEnrolmentDetailAnalyticsQuery } from './pathwayDetailAnalytics.codegen'
import {
  StatusProgressCompleted,
  StatusProgressDue,
  StatusProgressFailed,
  StatusProgressInProgress,
  StatusProgressNotStarted,
  StatusProgressPassed
} from 'front-end/src/components/common/status/Status'
import { getAttemptString } from 'front-end/src/pages/analytics/utils'
import { ButtonLight } from 'front-end/src/components'

interface IProps {
  topics: NonNullable<
    PathwayEnrolmentDetailAnalyticsQuery['pathwayEnrolment']
  >['topics']
  topicEnrolments: NonNullable<
    PathwayEnrolmentDetailAnalyticsQuery['pathwayEnrolment']
  >['topicEnrolments']
}

const PathwayDetailOverviewTable: FC<IProps> = ({
  topics,
  topicEnrolments
}) => {
  const [downloadingCSV, setDownloadingCSV] = useState<boolean>(false)
  const tableColumns: Array<Column<any>> = [
    {
      Header: t`Resource`,
      accessor: 'id',
      className: 'overflowVisible',
      Cell: (row) => {
        const userEnrolment = getUserEnrolment(row.value, topicEnrolments)
        const completion_date = userEnrolment
          ? userEnrolment.completion_date
          : null
        return (
          <>
            <span
              className={`statusCircle ${completion_date ? 'completed' : ''}`}
            >
              {completion_date && <IconTick />}
            </span>
            <strong className="dark overflowEllipsis">
              {userEnrolment?.topicVersion
                ? userEnrolment.topicVersion.title // topic version might be missing in imported workbooks
                : row.original.currentTopicVersion?.title}
            </strong>
          </>
        )
      }
    },
    {
      Header: t`Started`,
      maxWidth: 180,
      accessor: 'id',
      Cell: (row) => {
        const userEnrolment = getUserEnrolment(row.value, topicEnrolments)
        if (userEnrolment) {
          const { start_date } = userEnrolment
          return start_date ? formatDateShort(start_date) : null
        }
        return null
      }
    },
    {
      Header: t`Completed`,
      maxWidth: 160,
      accessor: 'id',
      Cell: (row) => {
        const userEnrolment = getUserEnrolment(row.value, topicEnrolments)
        if (userEnrolment) {
          const { completion_date } = userEnrolment
          return (
            <span className="green">
              {completion_date && formatDateShort(completion_date)}
            </span>
          )
        }
        return null
      }
    },
    {
      Header: t`Attempt`,
      maxWidth: 100,
      accessor: 'id',
      Cell: (row) => {
        const userEnrolment = getUserEnrolment(row.value, topicEnrolments)
        return (
          <span>
            {getAttemptString(
              userEnrolment?.attempt,
              userEnrolment?.topicVersion?.max_attempts,
              !!userEnrolment?.topicVersion?.allow_retry
            )}
          </span>
        )
      }
    },
    {
      Header: t`Score`,
      maxWidth: 100,
      className: 'center',
      headerClassName: 'center',
      accessor: 'id',
      Cell: (row) => {
        const userEnrolment = getUserEnrolment(row.value, topicEnrolments)
        const correct = userEnrolment ? userEnrolment.auto_correct : '0'
        const availablePoints = userEnrolment?.available_points || 0
        return availablePoints > 0 ? (
          <Box>
            <strong>{correct}</strong>/{availablePoints}
          </Box>
        ) : null
      }
    },
    {
      Header: t`Status`,
      accessor: 'id',
      maxWidth: 120,
      className: 'center result',
      headerClassName: 'center',
      Cell: (row) => {
        const userEnrolment = getUserEnrolment(row.value, topicEnrolments)
        if (userEnrolment) {
          return renderStatus(
            userEnrolment.status,
            userEnrolment.available_points || 0
          )
        } else {
          return null
        }
      }
    },
    {
      Header: t`Version`,
      accessor: 'id',
      sortable: false,
      width: 75,
      Cell: (row) => {
        const userEnrolment = getUserEnrolment(row.value, topicEnrolments)
        return userEnrolment?.status !== WorkbookStatusEnum.NOT_STARTED &&
          userEnrolment?.topicVersion?.version
          ? userEnrolment.topicVersion.version
          : ''
      }
    }
  ]

  const downloadCsv = async () => {
    try {
      const csv = papaparse.unparse(
        topics.map((row) => {
          const enrolment = getUserEnrolment(row.id, topicEnrolments)
          if (!enrolment) {
            return {
              Resource: row.currentTopicVersion?.title,
              Started: '',
              Completed: '',
              Score: '',
              Status: t`Not started`,
              Version: ''
            }
          }

          return {
            Resource: enrolment.topicVersion?.title,
            Started: formatDateShort(enrolment.start_date),
            Completed: formatDateShort(enrolment.completion_date),
            Score: `${enrolment.auto_correct ? enrolment.auto_correct : 0}/${
              enrolment.available_points
            }`,
            Status: enrolment.status,
            Version:
              enrolment?.status !== WorkbookStatusEnum.NOT_STARTED &&
              enrolment?.topicVersion?.version
                ? enrolment.topicVersion.version
                : ''
          }
        })
      )
      downloadAsFile(csv, 'looop-pathway-detail')
    } catch (err) {
      notification.error(t`CSV download failed`)
      throw err
    } finally {
      setDownloadingCSV(false)
    }
  }

  return (
    <TableExtendedOld
      data={topics}
      columns={tableColumns}
      className="-highlight AnalyticsTable AnalyticsTable__pathways"
      defaultPageSize={50}
      showPageSizeOptions={false}
      getTrProps={(_state, row) => ({
        onClick: () => {
          const userEnrolment = getUserEnrolment(
            row.original.id,
            topicEnrolments
          )
          userEnrolment &&
            navigateTo(
              linkBuilder.analyticsWorkbooksDetail(userEnrolment.id),
              true
            )
        },
        className:
          row && getUserEnrolment(row.original.id, topicEnrolments)
            ? 'clickable'
            : '',
        ...DEFAULT_TR_PROPS
      })}
      resizable={false}
      sortable={false}
      minRows={1}
      paginationContentAfter={
        <ButtonLight
          isLoading={downloadingCSV}
          disabled={downloadingCSV}
          onClick={async () => {
            setDownloadingCSV(true)
            await downloadCsv()
          }}
        >
          <Trans>Export data</Trans> (.csv)
        </ButtonLight>
      }
    />
  )
}

const getUserEnrolment = (
  topicId: number,
  topicEnrolments: NonNullable<
    PathwayEnrolmentDetailAnalyticsQuery['pathwayEnrolment']
  >['topicEnrolments']
) => {
  return topicEnrolments.find((te) => te.topic_id === topicId)
}

const renderStatus = (
  status: WorkbookStatusEnum | null | undefined,
  numberOfQuizzes: number
): ReactNode => {
  switch (status) {
    case WorkbookStatusEnum.FAILED:
      return <StatusProgressFailed />
    case WorkbookStatusEnum.OVERDUE:
      return <StatusProgressDue status={WorkbookStatusEnum.OVERDUE} />
    case WorkbookStatusEnum.COMPLETED:
      if (numberOfQuizzes > 0) {
        return <StatusProgressPassed />
      } else {
        return <StatusProgressCompleted />
      }
    case WorkbookStatusEnum.IN_PROGRESS:
      return <StatusProgressInProgress />
    case WorkbookStatusEnum.NOT_STARTED:
      return <StatusProgressNotStarted />
    default:
      return null
  }
}

export default PathwayDetailOverviewTable
